import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Select, ConfigProvider, Dropdown, Checkbox, Button, Popover, Space, Skeleton, Progress, Empty, Spin } from "antd";
import { useRequest } from "ahooks";
import { getFormattedWeekDates, k_weekType, usePlmStatuses, k_dateWidth } from "./board"
import type { WeekDate, RenderProjectItem, PopProjectItem } from "./board"
import { k_milestoneStatus } from "../detail/milestone/edit"
import { UserSvg, ArrowBottomSvg } from "@/components/svgs"
import { useParams } from "react-router-dom";
import { MilestoneEditStatusModal } from "@/components/project/milestone"
import {
    PPMService,
    getOverviewProjectsApi, getOverviewMilestonesApi, saveProjectEditMilestoneApi, saveOverviewConfigApi, getOverviewConfigApi,
    getOverviewProjectInfoApi, getProjectDashboardAPI
} from "@/services"
import { BoardRow } from "./boardRow";
import { PopProjects } from "./projectSelect";
import ActivityPop from "./activityPop"
import WorkpackagePop from "./workpackagePop"
import "./index.scss"
import clsx from "clsx";
import { documentUtil, toHsl, parseTime } from "@/utils"
import { set } from "lodash";


const weekOptions = Object.values(k_weekType).filter(value => typeof value === 'number').map((v) => ({
    label: `${v}周`,
    value: v 
}))

const Board: React.FC = () => {
    const navigate = useNavigate();
    const { projectID = "" } = useParams();
    const dateBlockRef = useRef<HTMLDivElement>(null);
    const [weekDate, setWeekDate] = useState(() => {
        let type = localStorage.getItem("ovweek")
        return type ? parseInt(type) : k_weekType.twoWeek
    });
    const onChangeWeekDate = (type: number) => {
        setWeekDate(type)
        localStorage.setItem("ovweek", type.toString())
    }
    const weeks = useMemo(() => {
        setTimeout(() => {
            if (dateBlockRef.current) {
                const container = dateBlockRef.current;
                const containerWidth = container.clientWidth;
                const scrollPosition = container.scrollWidth / 2 - containerWidth / 2;
                container.scrollTo({ left: scrollPosition });

            }
        }, 100)
        return getFormattedWeekDates(weekDate)
    }, [weekDate]);

    const [checkedIDs, setCheckedIDs] = useState<number[]>([]);

    const { data: milestones, loading: milestoneLoading, run: fetchMilestones, refresh: reFetchMilestones } = useRequest(getOverviewMilestonesApi, {
        manual: true,
    })
    const { runAsync: fetchOverviewConfig } = useRequest(getOverviewConfigApi, {
        manual: true,
    })
    const [hasPermission, setHasPermission] = useState(true)
    const { data: allProjects, loading: allProjectLoading, run: fetchProjects } = useRequest(getOverviewProjectsApi, {
        defaultParams: [projectID],
        onSuccess: async (data) => {
            let ids = data.filter(v => v.defaultSelected).map(v => v.id)
            try {
                const res = await fetchOverviewConfig(projectID)
                if (res.length > 0) {
                    const config = res[0];
                    if (config.projectIds && config.projectIds.length > 0) {
                        ids = config.projectIds;
                    }
                }
            } catch (error) {
                
            }
            setCheckedIDs(ids)
            if(ids.length === 0) return
            fetchMilestones({ projectIds: ids, startTime: weeks.startDate, endTime: weeks.endDate });
        }
    })
    const { data: projectInfo } = useRequest(PPMService.getProject, {
        defaultParams: [Number(projectID)],
        onError: (error: any) => {
            if(error.response.status === 404) {
                setHasPermission(false)
            }
            
        }
    })

    useEffect(() => {
        if (checkedIDs.length === 0) return
        fetchMilestones({ projectIds: checkedIDs, startTime: weeks.startDate, endTime: weeks.endDate });
        saveOverviewConfig(projectID, {
            autoSave: true,
            projectIds: checkedIDs,
        })
    }, [checkedIDs, weekDate]);
    
    const onCheckProjectsChange = (ids: number[]) => {
        setCheckedIDs(ids)
    }
    const [renderList, setRenderList] = useState<RenderProjectItem[]>([]);
    useEffect(() => {
        const list = (allProjects || []).filter((v) => (v.id && checkedIDs.includes(v.id))).map((v) => {
            return {
                ...v,
                milestones: (milestones || []).filter((m) => m.projectResp.id === v.id)
            }
        })
        setRenderList(list)
    }, [milestones, checkedIDs, allProjects]);
    const [projectPopOpen, setProjectPopOpen] = useState(false);

    const { run: saveOverviewConfig } = useRequest(saveOverviewConfigApi, {
        manual: true,
    })

    const [currentEditItem, setCurrentEditItem] = useState<API.PROJECT.OverviewMilestone & { _status: number, _statusLabel: string, _projectID: number }>()
    const [showCompletedDatePop, setShowCompletedDatePop] = useState(false)
    const [showProjectStatusWarnPop, setShowProjectStatusWarnPop] = useState(false)
    const onCancelEditStatus = () => {
        setShowCompletedDatePop(false)
        setShowProjectStatusWarnPop(false)
        setRenderList((vals) => {
            return vals.map(v => {
                if (currentEditItem && v.id === currentEditItem._projectID) {
                    return {
                        ...v,
                        milestones: v.milestones.map(v => {
                            return {
                                ...v,
                                status: currentEditItem._statusLabel, // 恢复之前的状态
                                statusCode: currentEditItem._status,
                            }
                        })
                    }
                }
                return v
            })
        })
        setCurrentEditItem(undefined)
    }
    const completedDate = useRef("")
    const { loading: saveLoading, run: runSaveItem } = useRequest(saveProjectEditMilestoneApi, {
        manual: true,
        onSuccess: () => {
            reFetchMilestones()
            setCurrentEditItem(undefined)
        }
    })
    const onSaveEditStatus = (date: string) => {
        if (saveLoading || !completedDate.current || !currentEditItem) return
        setShowCompletedDatePop(false)
        runSaveItem(currentEditItem._projectID, {
            code: currentEditItem.milestoneCode,
            milestoneName: currentEditItem.name,
            status: currentEditItem.statusCode,
            actualDate: date
        })
    }
    const { runAsync: updatePlmPackageStatus } = useRequest(PPMService.updatePlmPackageStatusAPI, {
        manual: true,
    })
    const onRowStatusChange = async (val: number, valLabel: string, item: API.PROJECT.OverviewMilestone, rowItem: RenderProjectItem, isPlm: boolean) => {
        if (isPlm) {
            await updatePlmPackageStatus(item.id, val, item.lockVersion)
            reFetchMilestones()
            return
        }
        if (val === item.statusCode || item.statusCode === 1) return
        setCurrentEditItem({
            ...item,
            _status: item.statusCode,
            _statusLabel: item.status,
            status: valLabel,
            statusCode: val,
            _projectID: rowItem.id
        })
        setRenderList((vals) => {
            return vals.map(v => {
                if (v.id === item.id) {
                    return {
                        ...v,
                        milestones: v.milestones.map(m => {
                            if (m.milestoneCode === item.milestoneCode) {
                                return {
                                    ...m,
                                    status: valLabel,
                                    statusCode: val
                                }
                            }
                            return m
                        })
                    }
                }
                return v
            })
        })
        if (val === 1) {
            const curDate = new Date()
            curDate.setHours(0, 0, 0)
            let completedDateStr = parseTime(new Date())
            if (item.startDate) {
                const planDate = new Date(item.startDate)
                planDate.setHours(0, 0, 0)
                if (planDate.getTime() < curDate.getTime()) {
                    completedDateStr = item.startDate
                }
            }
            completedDate.current = completedDateStr
            setShowCompletedDatePop(true)
        }
    }
    
    
    const createDataRef = useRef<{id: number, date: string}>()
    const [createPopVisible, setCreatePopVisible] = useState(false)

    const [packageItem, setPackageItem] = useState<API.PROJECT.OverviewMilestone>()

    const backDashboards = () => {
        navigate(`/projects/${projectID}/dashboard`);
    }

    if (!hasPermission) {
        return <>
            <Empty description="无权限查看该项目" />
        </>   
    }
    return (
        <ConfigProvider
            theme={{
                components: {
                    Button: {
                        colorPrimary: '#1f1547',
                    },
                Select: {
                    /* 这里是你的组件 token */
                },
            },
        }}
        >
            <div className="absolute left-0 right-0 top-[55px] bottom-0 bg-white z-10 flex flex-col px-2">
                <div className="shrink-0">
                    <div className="py-4 flex items-center relative border-0 border-b border-solid border-gray-200 font-bold text-base">
                        <div className="w-[4px] h-4 bg-blue-600 mr-2"></div>
                        <Button onClick={backDashboards} shape="round" size="small" className="mr-2 text-xs" icon={<span className="rotate-90 inline-block">
                            <ArrowBottomSvg />
                        </span>}>
                            返回
                        </Button>
                        多项目看板 - {projectInfo?.name}
                    </div>
                    <div className="flex p-2">
                        <div className="w-60 shrink-0 pl-4">
                            <Dropdown open={projectPopOpen} destroyOnHidden onOpenChange={(v) => setProjectPopOpen(v)} popupRender={() => <PopProjects value={checkedIDs} list={allProjects} onCheckedChange={onCheckProjectsChange} onHide={() => setProjectPopOpen(false)} />} trigger={['click']}>
                                <span className="border border-solid border-gray-300 py-1 px-3 rounded-md text-xs text-gray-700 cursor-pointer">+ 添加项目 <ArrowBottomSvg /></span>
                            </Dropdown>

                        </div>
                        <div className="flex justify-between items-center flex-1 pr-4">
                            <div className="text-xs font-bold">{weeks.startEndLabel}</div>
                            <div className="flex items-center">
                                <div className="mr-2">筛选器</div>
                                <div>
                                    <Select className="w-24" options={weekOptions} value={weekDate} onChange={onChangeWeekDate} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="overflow-x-auto flex-1" ref={dateBlockRef}>
                    <div className="w-max flex min-w-full sticky z-30 top-0 left-0 bg-white">
                        <div className="w-60 bg-white sticky z-40 left-0 top-0">
                            <div className="text-sm font-bold"></div>
                        </div>
                        {weeks.weekDates.map((v, i) => (
                            <div key={i} className={clsx("text-xs text-gray-400 text-center flex-1", v.isToday ? "font-bold text-gray-700" : "")} style={{ minWidth: k_dateWidth }}>{v.weekDate}</div>
                        ))}
                    </div>
                    {renderList.map((v, i) => (
                        <BoardRow weekdates={weeks.weekDates} data={v} key={i}
                            onStatusChange={(status, stausLabel, item, isPlm) => onRowStatusChange(status, stausLabel, item, v, isPlm)}
                            onCreate={(id, date) => {
                                createDataRef.current = {
                                    id: id,
                                    date: date
                                }
                                setCreatePopVisible(true)
                            }}
                            onPackageClick={setPackageItem}
                        />
                    ))}
                </div>
            </div>
            <MilestoneEditStatusModal visible={showCompletedDatePop} onCancel={onCancelEditStatus} onSave={onSaveEditStatus} completedDate={completedDate.current}></MilestoneEditStatusModal>
            {packageItem && (
                <ActivityPop projectID={packageItem.projectResp.id} packageID={packageItem.id} onClose={() => setPackageItem(undefined)} onUpdate={reFetchMilestones}></ActivityPop>
            )}
            <WorkpackagePop visible={createPopVisible} onClose={() => setCreatePopVisible(false)} projectID={createDataRef.current?.id} date={createDataRef.current?.date} onUpdate={reFetchMilestones}></WorkpackagePop>
        </ConfigProvider>
        
    );
};

export default Board;
